import {
  ICONFONT_SCRIPT_URL,
  ICONFONT_SCRIPT_URL_PATCHED,
} from './config.js';
import symbolOverrides from './symbol-overrides.json';

const ICONFONT_SCRIPT_RE = /font_3466273_[a-z0-9]+(?:\.patched)?\.js/;

function findIconfontScript() {
  if (typeof document === 'undefined') return null;
  return (
    document.head.querySelector('script[data-pmsmart-iconfont]') ||
    [...document.head.querySelectorAll('script[src]')].find((node) =>
      ICONFONT_SCRIPT_RE.test(node.getAttribute('src') || '')
    ) ||
    null
  );
}

function patchSymbolEntry(name, entry) {
  const sym =
    document.getElementById(`icon-${name}`) || document.querySelector(`symbol#icon-${name}`);
  if (!sym) return false;

  if (entry.symbolInner) {
    sym.innerHTML = entry.symbolInner;
    if (entry.viewBox) sym.setAttribute('viewBox', entry.viewBox);
    return true;
  }

  if (!entry.d) return false;

  const pathEl = sym.querySelector?.('path') || (sym.tagName === 'path' ? sym : null);
  if (pathEl) {
    pathEl.setAttribute('d', entry.d);
    return true;
  }
  return false;
}

function patchSymbolOverrides() {
  const symbols = symbolOverrides.symbols;
  if (!symbols || typeof document === 'undefined') return false;

  let patched = false;
  for (const [name, entry] of Object.entries(symbols)) {
    if (patchSymbolEntry(name, entry)) patched = true;

    const bufferKey = '_iconfont_svg_string_3466273';
    if (typeof window[bufferKey] !== 'string' || !window[bufferKey].includes(`id="icon-${name}"`)) {
      continue;
    }

    if (entry.symbolInner) {
      const viewBox = entry.viewBox || '0 0 1024 1024';
      window[bufferKey] = window[bufferKey].replace(
        new RegExp(`<symbol id="icon-${name}" viewBox="[^"]*">[\\s\\S]*?</symbol>`),
        `<symbol id="icon-${name}" viewBox="${viewBox}">${entry.symbolInner}</symbol>`
      );
      patched = true;
      continue;
    }

    if (entry.d) {
      window[bufferKey] = window[bufferKey].replace(
        new RegExp(`(id="icon-${name}" viewBox="[^"]*"><path d=")[^"]+`),
        `$1${entry.d}`
      );
      patched = true;
    }
  }
  return patched;
}

function watchSymbolOverrides() {
  if (typeof document === 'undefined' || !symbolOverrides.symbols) return;
  patchSymbolOverrides();
  if (Object.keys(symbolOverrides.symbols).every((name) => document.getElementById(`icon-${name}`))) {
    return;
  }
  const obs = new MutationObserver(() => {
    patchSymbolOverrides();
    const ready = Object.keys(symbolOverrides.symbols).every((name) =>
      document.getElementById(`icon-${name}`)
    );
    if (ready) obs.disconnect();
  });
  obs.observe(document.documentElement, { childList: true, subtree: true });
  setTimeout(() => obs.disconnect(), 8000);
}

/** Font 已更新但 Symbol JS CDN 滞后时，将 symbol-overrides.json 中的路径写入 DOM */
export function applySymbolOverrides() {
  watchSymbolOverrides();
}

function resolveIconfontUrl(url) {
  if (url) return url;
  const hasOverrides = symbolOverrides.symbols && Object.keys(symbolOverrides.symbols).length > 0;
  return hasOverrides ? ICONFONT_SCRIPT_URL_PATCHED : ICONFONT_SCRIPT_URL;
}

/**
 * 在浏览器中注入 iconfont Symbol 脚本（幂等）。
 * @param {string} [url] 覆盖默认 CDN（iconfont 重新发布后传入新链接）
 */
export function loadIconfont(url) {
  if (typeof document === 'undefined') return;
  const scriptUrl = resolveIconfontUrl(url);
  const existing = findIconfontScript();
  if (existing) {
    const currentSrc = existing.getAttribute('src') || '';
    const needsPatched =
      scriptUrl === ICONFONT_SCRIPT_URL_PATCHED && !currentSrc.includes('.patched.js');
    if (needsPatched) {
      existing.src = scriptUrl;
      existing.setAttribute('data-pmsmart-iconfont', 'true');
      existing.addEventListener('load', watchSymbolOverrides, { once: true });
      return;
    }
    if (scriptUrl && currentSrc !== scriptUrl && !currentSrc.endsWith(scriptUrl)) {
      existing.src = scriptUrl;
    }
    existing.setAttribute('data-pmsmart-iconfont', 'true');
    watchSymbolOverrides();
    return;
  }
  const script = document.createElement('script');
  script.src = scriptUrl;
  script.setAttribute('data-pmsmart-iconfont', 'true');
  script.crossOrigin = 'anonymous';
  script.addEventListener('load', watchSymbolOverrides, { once: true });
  document.head.appendChild(script);
}
